import { useEffect, useMemo, useState } from 'react';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TableCell from '@mui/material/TableCell';
import TablePagination from '@mui/material/TablePagination';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import CircularProgress from '@mui/material/CircularProgress';
import Box from '@mui/material/Box';
import Papa from 'papaparse';

type OriginRow = {
    SurveyCode: string;
    Postcode: string;
    Easting: string;
    Northing: string;
    TravelFrom: string;
    TravelTo: string;
};

export default function OriginsTable({ surveyCode }: { surveyCode: string }) {

    if (!surveyCode) return <Typography color="error">Missing ?survey= param.</Typography>;

    const [rows, setRows] = useState<OriginRow[]>([]);
    const [loading, setLoading] = useState(true);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRpp] = useState(25);

    useEffect(() => {
        let cancelled = false;
        setLoading(true);

        fetch("/TRAVLData/TravlOrigins.csv")
            .then(res => res.text())
            .then(csv => {
                const filteredRows: OriginRow[] = [];

                Papa.parse<OriginRow>(csv, {
                    header: true,
                    skipEmptyLines: true,
                    step: (row) => {
                        const record = row.data;
                        if (record.SurveyCode === surveyCode) {
                            filteredRows.push(record);
                        }
                    },
                    complete: () => {
                        if (cancelled) return;
                        setRows(filteredRows)
                        setLoading(false);
                    }
                });
            });

        return () => { cancelled = true; };
    }, [surveyCode]);

    const paged = useMemo(() => {
        const start = page * rowsPerPage;
        return rows.slice(start, start + rowsPerPage);
    }, [rows, page, rowsPerPage]);

    if (loading) {
        return (
            <Box sx={{ p: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                <CircularProgress size={18} /> <Typography>Loading origins�</Typography>
            </Box>
        );
    }

    if (!rows.length) return <Typography>No origins found for survey {surveyCode}.</Typography>;

    return (
        <Paper>
            <Typography sx={{ p: 2, pb: 0 }} variant="h6">Origins</Typography>
            <TableContainer>
                <Table size="small" sx={{
                    'td, th': { padding: '4px', fontSize: '0.73rem', whiteSpace: 'nowrap', maxWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis' }
                }}>
                    <TableHead>
                        <TableRow>
                            <TableCell>Postcode</TableCell>
                            <TableCell align="right">Easting</TableCell>
                            <TableCell align="right">Northing</TableCell>
                            <TableCell>Travel From %</TableCell>
                            <TableCell>Travel To %</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {paged.map((r, i) => (
                            <TableRow key={`${r.Postcode}-${i}`}>
                                <TableCell>{r.Postcode}</TableCell>
                                <TableCell align="right">{r.Easting}</TableCell>
                                <TableCell align="right">{r.Northing}</TableCell>
                                <TableCell>{r.TravelFrom}</TableCell>
                                <TableCell>{r.TravelTo}</TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>

            <TablePagination
                component="div"
                count={rows.length}
                page={page}
                onPageChange={(_, p) => setPage(p)}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={(e) => { setRpp(parseInt(e.target.value, 10)); setPage(0); }}
                rowsPerPageOptions={[10, 25, 50]}
            />
        </Paper>
    );
}
